import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, MarkerF, OverlayViewF, OverlayView } from '@react-google-maps/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Search, ChevronLeft, ChevronRight, MapPin, Monitor, Loader2, PlusCircle, CalendarIcon, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { VisiteDetailContent } from '../components/visites/VisiteDetailDialog';
import { VisiteFormContent } from '../components/visites/VisiteFormDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { disablePastDates } from '../lib/dateConstraints';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];

// 75001 Paris — always the center of the map
const PARIS_75001 = { lat: 48.8606, lng: 2.3376 };
const DEFAULT_ZOOM = 10;
const MAX_AUTO_ZOOM = 11;

const MAP_STYLES = [
    { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const JOURS_COMPLETS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MOIS_NOMS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS_SEMAINE = ['DIM.', 'LUN.', 'MAR.', 'MER.', 'JEU.', 'VEN.', 'SAM.'];

function formatDateLabel(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dateObj = new Date(d);
    dateObj.setHours(0, 0, 0, 0);
    let prefix = '';
    if (dateObj.getTime() === today.getTime()) prefix = "Aujourd'hui — ";
    else if (dateObj.getTime() === tomorrow.getTime()) prefix = 'Demain — ';
    return `${prefix}${JOURS_COMPLETS[d.getDay()]} ${d.getDate()} ${MOIS_NOMS[d.getMonth()]} ${d.getFullYear()}`;
}

function getDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function extractCity(adresse) {
    if (!adresse) return '';
    const parts = adresse.split(',');
    if (parts.length >= 2) {
        const cityPart = parts[parts.length - 2].trim();
        const cleaned = cityPart.replace(/^\d{5}\s*/, '');
        return cleaned || cityPart;
    }
    return '';
}

function getVisiteColor(v) {
    if (v.commercial_id && v.commercial_couleur) return v.commercial_couleur;
    return '#ef4444';
}


export function PlanifierVisitePage() {
    const navigate = useNavigate();

    // --- Map state (presentielle) ---
    const [selectedDate, setSelectedDate] = useState(() => getDateStr(new Date()));
    const [visites, setVisites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchMarker, setSearchMarker] = useState(null);
    const [geocodedVisites, setGeocodedVisites] = useState([]);
    // distanceMatrix[visiteId] = { distanceText, drivingText, transitText }
    const [distanceMatrix, setDistanceMatrix] = useState({});
    const [loadingDistances, setLoadingDistances] = useState(false);
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);
    const searchInputRef = useRef(null);

    // --- Virtual visits state ---
    const [virtuelleVisites, setVirtuelleVisites] = useState([]);
    const [loadingVirtuelle, setLoadingVirtuelle] = useState(false);

    // --- Dialog state (shared) ---
    const [selectedVisite, setSelectedVisite] = useState(null);
    const [dialogMode, setDialogMode] = useState(null);
    const [prospectConfig, setProspectConfig] = useState({});
    const [commerciaux, setCommerciaux] = useState([]);

    // --- Visite programmée state ---
    const [visiteProgrammee, setVisiteProgrammee] = useState(false);
    const [preTypeVisite, setPreTypeVisite] = useState('');
    const [preDateVisite, setPreDateVisite] = useState(null);
    const [preDateOpen, setPreDateOpen] = useState(false);
    const [preHeureVisite, setPreHeureVisite] = useState('');

    const [showReprogrammerModal, setShowReprogrammerModal] = useState(false);
    const [reprogrammerVisite, setReprogrammerVisite] = useState(null);
    const [reprogrammerRemarque, setReprogrammerRemarque] = useState('');
    const [reprogrammerSaving, setReprogrammerSaving] = useState(false);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_KEY,
        libraries: LIBRARIES,
    });

    useEffect(() => {
        axios.get(`${API}/api/prospects/config`, { withCredentials: true })
            .then(({ data }) => setProspectConfig(data)).catch(() => {});
        axios.get(`${API}/api/admin/users`, { withCredentials: true })
            .then(({ data }) => setCommerciaux(data.filter(u => u.couleur && u.role === 'commercial'))).catch(() => {});
    }, []);

    // Fetch presentielle visites
    const fetchVisites = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/visites`, {
                params: { type_visite: 'presentielle', date: selectedDate },
                withCredentials: true
            });
            setVisites(data.filter(v => v.statut !== 'annulee' && v.statut !== 'decalee'));
        } catch {
            toast.error('Erreur lors du chargement des visites');
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    // Fetch virtuelle visites
    const fetchVirtuelleVisites = useCallback(async () => {
        setLoadingVirtuelle(true);
        try {
            const { data } = await axios.get(`${API}/api/visites`, {
                params: { type_visite: 'virtuelle' },
                withCredentials: true
            });
            setVirtuelleVisites(data.filter(v => v.statut !== 'annulee' && v.statut !== 'decalee'));
        } catch {
            toast.error('Erreur lors du chargement des visites virtuelles');
        } finally {
            setLoadingVirtuelle(false);
        }
    }, []);

    useEffect(() => { fetchVisites(); }, [fetchVisites]);
    useEffect(() => { fetchVirtuelleVisites(); }, [fetchVirtuelleVisites]);

    // Grouped virtual visits for selected date
    const virtuellesForDate = useMemo(() => {
        return virtuelleVisites.filter(v => v.date === selectedDate).sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
    }, [virtuelleVisites, selectedDate]);

    // Geocode visit addresses
    useEffect(() => {
        if (!isLoaded || !visites.length) { setGeocodedVisites([]); return; }
        const geocoder = new window.google.maps.Geocoder();
        const promises = visites.map(v => {
            const adresse = v.prospect_adresse_depart?.adresse;
            if (!adresse) return Promise.resolve(null);
            return new Promise(resolve => {
                geocoder.geocode({ address: adresse }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                        resolve({ ...v, lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng(), city: extractCity(adresse) });
                    } else { resolve(null); }
                });
            });
        });
        Promise.all(promises).then(results => setGeocodedVisites(results.filter(Boolean)));
    }, [visites, isLoaded]);

    // Setup autocomplete
    useEffect(() => {
        if (!isLoaded || !searchInputRef.current || autocompleteRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            componentRestrictions: { country: 'fr' },
            fields: ['geometry', 'formatted_address'],
        });
        ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            if (place.geometry) {
                setSearchMarker({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), address: place.formatted_address });
            }
        });
        autocompleteRef.current = ac;
    }, [isLoaded]);

    // Auto-zoom : ajuste la carte pour voir TOUS les points (visites + adresse saisie).
    // Sans points → centre Paris zoom par défaut.
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return;
        const allPoints = geocodedVisites.map(v => ({ lat: v.lat, lng: v.lng }));
        if (searchMarker) allPoints.push({ lat: searchMarker.lat, lng: searchMarker.lng });
        if (allPoints.length === 0) {
            mapRef.current.setCenter(PARIS_75001);
            mapRef.current.setZoom(DEFAULT_ZOOM);
            return;
        }
        if (allPoints.length === 1) {
            // 1 seul point → centrer dessus avec un zoom moyen
            mapRef.current.setCenter(allPoints[0]);
            mapRef.current.setZoom(13);
            return;
        }
        // 2+ points : fitBounds réel sur les points (sans forcer Paris)
        const bounds = new window.google.maps.LatLngBounds();
        allPoints.forEach(p => bounds.extend(p));
        mapRef.current.fitBounds(bounds, { top: 80, bottom: 80, left: 80, right: 80 });
        // Plafond de zoom (anti-overshoot quand 2 points sont très proches)
        const listener = window.google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
            const zoom = mapRef.current.getZoom();
            if (zoom > 15) mapRef.current.setZoom(15);
        });
        return () => window.google.maps.event.removeListener(listener);
    }, [geocodedVisites, searchMarker, isLoaded]);

    // ---------------------------------------------------------------------
    // Distance Matrix : distance + temps voiture + temps transport en commun
    // depuis l'adresse recherchee vers chaque visite presentielle.
    // Appel batch (1 origine, N destinations) puis 2e batch en mode 'TRANSIT'.
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (!isLoaded || !searchMarker || geocodedVisites.length === 0) {
            setDistanceMatrix({});
            return;
        }
        setLoadingDistances(true);
        const service = new window.google.maps.DistanceMatrixService();
        const origin = { lat: searchMarker.lat, lng: searchMarker.lng };
        const destinations = geocodedVisites.map(v => ({ lat: v.lat, lng: v.lng }));
        const matrix = {};
        const callMatrix = (mode) => new Promise((resolve) => {
            service.getDistanceMatrix({
                origins: [origin],
                destinations,
                travelMode: mode,
                unitSystem: window.google.maps.UnitSystem.METRIC,
                ...(mode === 'TRANSIT' ? { transitOptions: { departureTime: new Date() } } : {}),
            }, (response, status) => {
                if (status !== 'OK' || !response?.rows?.[0]) { resolve({}); return; }
                const row = response.rows[0].elements;
                const out = {};
                geocodedVisites.forEach((v, i) => {
                    const el = row[i];
                    if (!el || el.status !== 'OK') return;
                    out[v.id] = {
                        distanceText: el.distance?.text || '',
                        distanceValue: el.distance?.value || 0,
                        durationSec: el.duration?.value || 0,
                    };
                });
                resolve(out);
            });
        });
        // Format compact : 1h30, 0h25, 2h05
        const fmt = (sec) => {
            if (!sec || sec <= 0) return '';
            const totalMin = Math.round(sec / 60);
            const h = Math.floor(totalMin / 60);
            const m = totalMin % 60;
            return `${h}h${String(m).padStart(2, '0')}`;
        };
        Promise.all([callMatrix('DRIVING'), callMatrix('TRANSIT')]).then(([driving, transit]) => {
            geocodedVisites.forEach(v => {
                matrix[v.id] = {
                    distanceText: driving[v.id]?.distanceText || transit[v.id]?.distanceText || '',
                    distanceValue: driving[v.id]?.distanceValue ?? transit[v.id]?.distanceValue ?? 0,
                    drivingText: fmt(driving[v.id]?.durationSec),
                    transitText: fmt(transit[v.id]?.durationSec),
                };
            });
            setDistanceMatrix(matrix);
            setLoadingDistances(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchMarker, geocodedVisites.length, isLoaded]);


    // ---------------------------------------------------------------------
    // Anti-chevauchement des labels sur la carte
    // ---------------------------------------------------------------------
    // On calcule en pixels la position de chaque marqueur et on tente 8 positions
    // candidates autour. La 1ere position libre (sans collision avec un label
    // deja place) est retenue. Recalcule sur zoom/pan ('idle').
    const [labelOffsets, setLabelOffsets] = useState({});
    useEffect(() => {
        if (!isLoaded || !mapRef.current || geocodedVisites.length === 0) {
            setLabelOffsets({});
            return;
        }
        const map = mapRef.current;
        const recompute = () => {
            const proj = map.getProjection?.();
            const bounds = map.getBounds?.();
            const zoom = map.getZoom?.();
            if (!proj || !bounds || zoom == null) return;
            const scale = Math.pow(2, zoom);
            const swPx = proj.fromLatLngToPoint(bounds.getSouthWest());
            const nePx = proj.fromLatLngToPoint(bounds.getNorthEast());
            const toPx = (lat, lng) => {
                const p = proj.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
                return { x: (p.x - swPx.x) * scale, y: (p.y - nePx.y) * scale };
            };
            // dimension approx d'un label (Helvetica 11 + padding)
            const measure = (v) => {
                const text = `${v.heure} | ${v.prospect_nom || 'Client'}`;
                return { w: text.length * 6.8 + 22, h: 22 };
            };
            // 8 positions candidates : dx,dy en proportion (label-width, label-height)
            const CANDIDATES = [
                { dx: -0.5, dy: -1.35 },  // top center
                { dx: -0.5, dy:  0.55 },  // bottom center
                { dx:  0.20, dy: -0.5 },  // right
                { dx: -1.20, dy: -0.5 },  // left
                { dx:  0.20, dy: -1.35 }, // top-right
                { dx: -1.20, dy: -1.35 }, // top-left
                { dx:  0.20, dy:  0.55 }, // bottom-right
                { dx: -1.20, dy:  0.55 }, // bottom-left
            ];
            const placed = [];
            const result = {};
            for (const v of geocodedVisites) {
                const c = toPx(v.lat, v.lng);
                const { w, h } = measure(v);
                let chosen = CANDIDATES[0];
                for (const cand of CANDIDATES) {
                    const x = c.x + cand.dx * w;
                    const y = c.y + cand.dy * h;
                    const overlap = placed.some(p => !(x + w < p.x || p.x + p.w < x || y + h < p.y || p.y + p.h < y));
                    if (!overlap) {
                        chosen = cand;
                        placed.push({ x, y, w, h });
                        break;
                    }
                }
                // Si toutes collisionnent on garde la 1ere (top center) — au moins lisible
                if (!placed.length || placed[placed.length - 1].x !== c.x + chosen.dx * w) {
                    placed.push({ x: c.x + chosen.dx * w, y: c.y + chosen.dy * h, w, h });
                }
                result[v.id] = chosen;
            }
            setLabelOffsets(result);
        };
        recompute();
        const l1 = window.google.maps.event.addListener(map, 'idle', recompute);
        return () => window.google.maps.event.removeListener(l1);
    }, [isLoaded, geocodedVisites]);

    const prevDay = () => {
        const d = new Date(selectedDate + 'T00:00:00');
        d.setDate(d.getDate() - 1);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (d >= today) setSelectedDate(getDateStr(d));
    };
    const nextDay = () => {
        const d = new Date(selectedDate + 'T00:00:00');
        d.setDate(d.getDate() + 1);
        setSelectedDate(getDateStr(d));
    };
    const goToday = () => setSelectedDate(getDateStr(new Date()));
    const isToday = selectedDate === getDateStr(new Date());

    // Dialog handlers
    const handleCloseDialog = () => { setDialogMode(null); setSelectedVisite(null); };
    const handleEditSuccess = async () => {
        await fetchVisites();
        await fetchVirtuelleVisites();
        if (selectedVisite) {
            try {
                const { data } = await axios.get(`${API}/api/visites/${selectedVisite.id}`, { withCredentials: true });
                setSelectedVisite(data);
            } catch {}
        }
        setDialogMode('detail');
    };
    const handleDetailRefresh = async () => {
        await fetchVisites();
        await fetchVirtuelleVisites();
        if (selectedVisite) {
            try {
                const { data } = await axios.get(`${API}/api/visites/${selectedVisite.id}`, { withCredentials: true });
                setSelectedVisite(data);
            } catch {}
        }
    };

    const mapContainerStyle = { width: '100%', height: '100%' };

    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center h-[80vh]">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    return (
        <div className="flex flex-col" data-testid="planifier-page">
            {/* Header */}
            <div className="shrink-0 p-3 md:p-4 border-b bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="planifier-back-btn">
                            <ArrowLeft size={16} className="mr-1" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="planifier-title">
                            Planifier une visite
                        </h1>
                    </div>
                </div>

                {/* Search bar + Visite programmée + Ajouter button */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500 z-10" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder=""
                            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                            data-testid="planifier-search-input"
                        />
                        {searchMarker && (
                            <button onClick={() => { setSearchMarker(null); if (searchInputRef.current) searchInputRef.current.value = ''; }} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600" data-testid="planifier-clear-search">
                                Effacer
                            </button>
                        )}
                    </div>

                    {/* Toggle Visite programmée */}
                    <div className="flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                        <Label htmlFor="visite-programmee" className="text-xs font-medium text-slate-600 cursor-pointer whitespace-nowrap" data-testid="visite-programmee-label">Programmation visite</Label>
                        <Switch
                            id="visite-programmee"
                            checked={visiteProgrammee}
                            onCheckedChange={(v) => {
                                setVisiteProgrammee(v);
                                if (!v) { setPreTypeVisite(''); setPreDateVisite(null); setPreHeureVisite(''); }
                            }}
                            data-testid="visite-programmee-toggle"
                        />
                    </div>

                    <Button
                        onClick={() => {
                            const adresse = searchInputRef.current?.value || '';
                            if (visiteProgrammee) {
                                if (!preTypeVisite || !preDateVisite || !preHeureVisite) {
                                    toast.error('Veuillez remplir le type, la date et l\'heure de la visite');
                                    return;
                                }
                                sessionStorage.setItem('visite_prefill', JSON.stringify({
                                    type_visite: preTypeVisite,
                                    date: preDateVisite,
                                    heure: preHeureVisite
                                }));
                            }
                            navigate('/prospects?action=nouveau', { state: { adresse_depart: adresse } });
                        }}
                        className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white shrink-0 px-5 py-2 text-sm"
                        data-testid="planifier-ajouter-btn"
                    >
                        <PlusCircle size={18} className="mr-1.5" />
                        Ajouter
                    </Button>
                </div>

                {/* Champs visite programmée */}
                {visiteProgrammee && (
                    <div className="flex items-center gap-3 mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex items-center gap-2">
                            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Type</Label>
                            <Select value={preTypeVisite || undefined} onValueChange={setPreTypeVisite}>
                                <SelectTrigger className="w-[160px] h-8 text-xs bg-white" data-testid="pre-type-visite-select">
                                    <SelectValue placeholder="Type de visite" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="presentielle">Présentielle</SelectItem>
                                    <SelectItem value="virtuelle">Virtuelle</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Date</Label>
                            <Popover open={preDateOpen} onOpenChange={setPreDateOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 text-xs bg-white w-[150px] justify-start" data-testid="pre-date-visite-btn">
                                        <CalendarIcon size={14} className="mr-1.5 text-slate-400" />
                                        {preDateVisite ? format(new Date(preDateVisite + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr }) : 'Choisir'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={preDateVisite ? new Date(preDateVisite + 'T00:00:00') : undefined}
                                        onSelect={(d) => { setPreDateVisite(d ? getDateStr(d) : null); setPreDateOpen(false); }}
                                        locale={fr}
                                        disabled={disablePastDates}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="flex items-center gap-2">
                            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Heure</Label>
                            <Input
                                type="time"
                                value={preHeureVisite}
                                onChange={(e) => setPreHeureVisite(e.target.value)}
                                className="w-[110px] h-8 text-xs bg-white"
                                data-testid="pre-heure-visite-input"
                            />
                        </div>
                    </div>
                )}

            </div>

            {/* Date navigation - full width above map+panel */}
            <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                <button onClick={prevDay} disabled={isToday} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm" data-testid="planifier-prev-day">
                    <ChevronLeft size={22} className="text-slate-700" />
                </button>
                <button onClick={nextDay} className="p-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-100 hover:border-slate-400 transition-all shadow-sm" data-testid="planifier-next-day">
                    <ChevronRight size={22} className="text-slate-700" />
                </button>
                {!isToday && (
                    <button onClick={goToday} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#2e7cd6] text-white hover:bg-[#2568b8] transition-colors" data-testid="planifier-today-btn">
                        Aujourd'hui
                    </button>
                )}
                <p className="text-base font-bold text-slate-800" data-testid="planifier-date-label" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatDateLabel(selectedDate)}
                </p>
            </div>

            {/* Content: Map full width (carrée) + Virtual visits below */}
            <div className="flex flex-col">
                {/* Map - ratio 16:10 (un peu moins haute) */}
                <div className="relative w-full aspect-[16/10]">
                    {(loading || loadingDistances) && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 shadow-md border border-slate-200 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-[#2e7cd6]" />
                            <span className="text-xs font-medium text-slate-700">
                                {loadingDistances ? 'Calcul des distances...' : 'Chargement...'}
                            </span>
                        </div>
                    )}
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={PARIS_75001}
                        zoom={DEFAULT_ZOOM}
                        onLoad={(map) => { mapRef.current = map; }}
                        options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            zoomControl: true,
                            zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
                            styles: MAP_STYLES,
                        }}
                    >
                        {geocodedVisites.map((v) => (
                            <MarkerF
                                key={v.id}
                                position={{ lat: v.lat, lng: v.lng }}
                                onClick={() => { setSelectedVisite(v); setDialogMode('detail'); }}
                                icon={{ path: window.google.maps.SymbolPath.CIRCLE, fillColor: getVisiteColor(v), fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2, scale: 10 }}
                            />
                        ))}
                        {geocodedVisites.map((v) => {
                            const off = labelOffsets[v.id] || { dx: -0.5, dy: -1.35 };
                            const dist = distanceMatrix[v.id];
                            return (
                                <OverlayViewF key={`label-${v.id}`} position={{ lat: v.lat, lng: v.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                                    <div style={{ transform: `translate(${off.dx * 100}%, ${off.dy * 100}%)`, whiteSpace: 'nowrap', border: `1.5px solid ${getVisiteColor(v)}`, borderRadius: '6px' }} className="bg-white shadow-sm px-1.5 py-0.5 pointer-events-none">
                                        <p className="text-[11px] leading-tight font-semibold text-slate-800">{v.heure} | {v.prospect_nom || 'Client'}</p>
                                        {dist && (dist.drivingText || dist.transitText) && (
                                            <p className="text-[10px] leading-tight text-slate-600 flex items-center gap-1 mt-0.5">
                                                {dist.distanceText && <span>📍{dist.distanceText}</span>}
                                                {dist.drivingText && <span>🚗{dist.drivingText}</span>}
                                                {dist.transitText && <span>🚌{dist.transitText}</span>}
                                            </p>
                                        )}
                                    </div>
                                </OverlayViewF>
                            );
                        })}
                        {searchMarker && (
                            <MarkerF
                                position={{ lat: searchMarker.lat, lng: searchMarker.lng }}
                                icon={{
                                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.06 27.94 0 18 0z" fill="#f97316"/><circle cx="18" cy="18" r="7" fill="white"/></svg>'),
                                    scaledSize: new window.google.maps.Size(28, 37),
                                    anchor: new window.google.maps.Point(14, 37),
                                }}
                            />
                        )}
                    </GoogleMap>
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-slate-200">
                        <p className="text-xs font-medium text-slate-700" data-testid="planifier-visit-count">
                            {geocodedVisites.length} visite{geocodedVisites.length !== 1 ? 's' : ''} présentielle{geocodedVisites.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                {/* Virtual visits panel - bottom (apparaît quand on scrolle) */}
                <div className="border-t border-slate-200 bg-gradient-to-b from-violet-50/50 to-white flex flex-col" data-testid="planifier-virtuelle-panel">
                    {/* Panel header */}
                    <div className="shrink-0 px-5 py-2.5 border-b border-violet-100 bg-violet-50/80">
                        <div className="flex items-center gap-2.5">
                            <div className="p-1.5 rounded-lg bg-violet-100">
                                <Monitor size={16} className="text-violet-600" />
                            </div>
                            <h2 className="text-sm font-bold text-slate-800">Visites virtuelles</h2>
                            <p className="text-xs text-slate-500">· {virtuellesForDate.length} programmée{virtuellesForDate.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>

                    {/* Panel content (horizontal scroll) */}
                    <div className="flex-1 overflow-x-auto overflow-y-hidden">
                        {loadingVirtuelle ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
                            </div>
                        ) : virtuellesForDate.length === 0 ? (
                            <div className="flex items-center gap-3 px-5 py-6 text-slate-400">
                                <Monitor size={20} className="text-violet-300" />
                                <p className="text-sm">Aucune visite virtuelle pour cette date</p>
                            </div>
                        ) : (
                            <div className="flex gap-2.5 p-3 min-w-min">
                                {virtuellesForDate.map((v) => {
                                    const color = getVisiteColor(v);
                                    return (
                                        <button
                                            key={v.id}
                                            onClick={() => { setSelectedVisite(v); setDialogMode('detail'); }}
                                            className="shrink-0 w-[230px] text-left rounded-xl px-3 py-2.5 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-sm hover:shadow-md bg-white border-2"
                                            style={{ borderColor: color }}
                                            data-testid={`virtuelle-visit-${v.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                                    <p className="text-sm font-semibold text-slate-800 truncate">{v.prospect_nom || 'Client'}</p>
                                                </div>
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white ml-2 shrink-0" style={{ backgroundColor: color }}>{v.heure}</span>
                                            </div>
                                            {v.commercial_name && (
                                                <p className="text-xs mt-1.5 truncate" style={{ color: color }}>{v.commercial_name}</p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Légende des couleurs */}
            {commerciaux.length > 0 && (
                <div className="shrink-0 px-4 py-3 bg-white border-t border-slate-200" data-testid="planifier-legende">
                    <div className="flex items-center gap-5 flex-wrap">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Légende</span>
                        {commerciaux.map(c => (
                            <div key={c.id} className="flex items-center gap-2">
                                <span className="w-3.5 h-3.5 rounded-full shrink-0 border border-white shadow-sm" style={{ backgroundColor: c.couleur }} />
                                <span className="text-sm text-slate-700">{c.name}</span>
                            </div>
                        ))}
                        <div className="flex items-center gap-2">
                            <span className="w-3.5 h-3.5 rounded-full shrink-0 border border-white shadow-sm" style={{ backgroundColor: '#ef4444' }} />
                            <span className="text-sm text-red-600 font-medium">Non assigné</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Visite Dialog (shared) */}
            <Dialog open={dialogMode !== null} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
                <DialogContent
                    className="max-w-5xl w-full max-h-[90dvh] flex flex-col p-0 overflow-hidden"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    hideCloseButton
                    data-testid="planifier-visite-dialog"
                >
                    <DialogTitle className="sr-only">Visite</DialogTitle>
                    {dialogMode === 'detail' && selectedVisite && (
                        <VisiteDetailContent
                            visite={selectedVisite}
                            onClose={handleCloseDialog}
                            onEdit={() => setDialogMode('edit')}
                            onRefresh={handleDetailRefresh}
                            config={prospectConfig}
                            onReprogrammerRequest={(visite) => {
                                handleCloseDialog();
                                setTimeout(() => {
                                    setReprogrammerVisite(visite);
                                    setReprogrammerRemarque('');
                                    setShowReprogrammerModal(true);
                                }, 200);
                            }}
                            onReplanifierRequest={(visite) => {
                                try {
                                    sessionStorage.setItem('visite_replanif_status', 'a_venir');
                                } catch { /* ignore */ }
                                setDialogMode('edit');
                            }}
                        />
                    )}
                    {dialogMode === 'edit' && selectedVisite && (
                        <VisiteFormContent
                            visite={selectedVisite}
                            prospect={{ id: selectedVisite.prospect_id, nom: selectedVisite.prospect_nom }}
                            onSuccess={handleEditSuccess}
                            onCancel={() => setDialogMode('detail')}
                            config={prospectConfig}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal Reprogrammer visite */}
            {showReprogrammerModal && reprogrammerVisite && (
                <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="reprogrammer-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Reprogrammer la visite — {reprogrammerVisite.prospect_nom}
                            </h2>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-500">Une tâche sera créée automatiquement pour l'assistante.</p>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Remarque</label>
                                <textarea
                                    value={reprogrammerRemarque}
                                    onChange={e => setReprogrammerRemarque(e.target.value)}
                                    rows={3}
                                    className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2e7cd6] focus:border-transparent resize-none"
                                    placeholder="Ajouter une remarque..."
                                    autoFocus
                                    data-testid="reprogrammer-remarque"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                            <Button variant="outline" size="sm" onClick={() => { setShowReprogrammerModal(false); setReprogrammerVisite(null); }}>Annuler</Button>
                            <Button
                                size="sm"
                                className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                disabled={reprogrammerSaving}
                                data-testid="reprogrammer-confirm-btn"
                                onClick={async () => {
                                    setReprogrammerSaving(true);
                                    try {
                                        await axios.put(`${API}/api/visites/${reprogrammerVisite.id}`, { statut: 'decalee' }, { withCredentials: true });
                                        await axios.post(`${API}/api/taches`, {
                                            client_nom: reprogrammerVisite.prospect_nom,
                                            titre: 'Reprogrammer la visite',
                                            personne_concernee: 'Assistante',
                                            remarque: reprogrammerRemarque,
                                            statut: 'a_traiter',
                                            linked_visite_id: reprogrammerVisite.id,
                                            type_rappel: 'visite_reprogrammer',
                                        }, { withCredentials: true });
                                        toast.success('Visite à reprogrammer — tâche créée');
                                        setShowReprogrammerModal(false);
                                        setReprogrammerVisite(null);
                                        fetchVisites();
                                    } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Erreur');
                                    } finally { setReprogrammerSaving(false); }
                                }}
                            >
                                {reprogrammerSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Confirmer
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
